/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Auth0Client, RedirectLoginResult, IdToken, GetTokenSilentlyOptions, ConnectAccountRedirectResult } from '@auth0/auth0-spa-js';
import { AuthError } from "./AuthError";
import { BAVEL_PATH } from '@config/app-config';
import { BavelUser } from '@account/models/bavel-user.model';
import { EncryptionService } from '@shared/encryption/services/encryption.service';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { IFeatureToggleResponse } from '@shared/interfaces/feature-toggle-response.interface';
import { inject, Injectable } from '@angular/core';
import { IUserProfileInfo } from '@shared/interfaces/user-profile-info.interface';
import { IUsersService } from '../interfaces/users-service.interface';
import { LoginResponse } from '@account/models/login-response.model';
import { LogService } from '@logs/services/log.service';
import { map } from 'rxjs/operators';
import { Observable, Observer, of, Subject } from 'rxjs';
import { PaymentApibuild } from '@utils/api-url-builder';
import { Router } from '@angular/router';
import { URLS } from '@account/bavel-account.config';

const PROFILE_API_URL: string = PaymentApibuild(['api', 'Account'], 'get-login-profile-info');
const SERVICE_NAME: string = 'Shared/UsersService';
const SESSION_ID: string = 'session_id';

@Injectable()
export class Auth0UsersService implements IUsersService {
  public currentUser!: BavelUser;
  public userProfile!: IUserProfileInfo;
  private onLoggedIn: Subject<void> = new Subject();
  public onLoggedIn$: Observable<void> = this.onLoggedIn.asObservable();

  private encryptionService: EncryptionService = inject(EncryptionService);
  private logService: LogService = inject(LogService);
  private router: Router = inject(Router);
  private http: HttpClient = inject(HttpClient);
  private auth0Client: Auth0Client = inject(Auth0Client);

  public get loggedUsername(): string {
    return this.currentUser?.name;
  }

  public get isImpersonated(): boolean {
    return this.currentUser?.isImpersonated;
  }

  public get sessionId(): string {
    return window.localStorage.getItem(SESSION_ID) || '';
  }

  public storeUserProfile(loginResponse: LoginResponse): void {
    const encryptedRoles = loginResponse.roles.map(role => this.encryptionService.encrypt(role));
    this.currentUser = {
      alienetAliasName: loginResponse.alienetAliasName,
      alienetAliasScoperId: loginResponse.alienetAliasScoperId,
      canChangeUser: loginResponse.canChangeUser,
      id: loginResponse.userId,
      idChk: loginResponse.userIdChk,
      loginId: loginResponse.loginId,
      name: loginResponse.username,
      roles: encryptedRoles,
      isImpersonated: loginResponse.isImpersonated
    };
  }

  public signoutRedirect(): void {
    this.auth0Client.logout({
      logoutParams: { returnTo: environment.auth0.logoutUri }
    });
  }

  public getProfile(): Observable<IUserProfileInfo> {
    this.logService.logInfo(`[${SERVICE_NAME}] Retrieving user profile info`);
    return (this.userProfile) ? of<IUserProfileInfo>(this.userProfile) :
      this.http.get<IUserProfileInfo>(PROFILE_API_URL)
        .pipe(
          map(this.mapProfile.bind(this))
        );
  }

  public getUserId(): number {
    return this.currentUser.id;
  }

  public userHasRole(role: string): boolean {
    return this.currentUser ?
      this.currentUser.roles.some(encryptedRole => this.encryptionService.decrypt(encryptedRole) === role) : false;
  }

  public get isUserProfileAvailable(): Observable<boolean> {
    return of(this.currentUser !== null && typeof this.currentUser !== 'undefined');
  }
  public signInRedirect(returnUrl: string): void {
    this.auth0Client.loginWithRedirect({
      appState: { returnUrl: returnUrl },
      authorizationParams: this.getAuthorizationParams()
    });
  }

  public completeAuthentication(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.auth0Client.handleRedirectCallback().then((result: RedirectLoginResult<any> | ConnectAccountRedirectResult<any>) => {
        this.auth0Client.getIdTokenClaims().then((f: IdToken | undefined) => {
          this.auth0Client.getTokenSilently(this.GetTokenOptions()).then(() => {
            if (f?.sid == undefined) {
              this.logService.logCritical('unable to get sid');
              reject(new Error('unable to get sid'));
              return;
            }
            window.localStorage.setItem(SESSION_ID, f?.sid);
            this.onLoggedIn.next();

            if (result.appState != undefined && result.appState.returnUrl != undefined) {
              this.router.navigateByUrl(result.appState.returnUrl);
              resolve();
              return;
            }
            this.router.navigate(['/']); // Navigate to your default route
            resolve();

          }).catch((error) => {
            this.logService.logCritical('Unable to getTokenSilently on complete authentication.' + error); reject(error);
          });
        }).catch((error) => {
          this.logService.logCritical('Unable to getIdTokenClaims.' + error); reject(error);
        });
      }).catch((error) => {
        const authError = AuthError.from(error);
        this.router.navigate([BAVEL_PATH.AUTHENTICATIONFAILED], { queryParams: authError });
        this.logService.logCritical('Unable to handleRedirectCallback.' + error);
        reject(error);
      });
    });
  }

  public canChangeUser(): boolean {
    return this.currentUser.canChangeUser;
  }

  public isPartnerPortalDigitalArchiveEnabled(): Observable<IFeatureToggleResponse> {
    this.logService.logInfo(`[${SERVICE_NAME}] Get isPartnerPortalDigitalArchiveEnabled`);
    return this.http.get<IFeatureToggleResponse>(URLS.IS_PARTNER_PORTAL_DIGITAL_ARCHIVE_ENABLED);
  }

  private mapProfile(profile: IUserProfileInfo): IUserProfileInfo {
    this.userProfile = profile;
    return profile;
  }

  private GetTokenOptions(): GetTokenSilentlyOptions {
    return { authorizationParams: this.getAuthorizationParams() };
  }

  private getAuthorizationParams(): any {
    return {
      redirect_uri: environment.auth0.redirectUri,
      audience: environment.auth0.audience,
      scope: environment.auth0.scope
    };
  }

  public shouldLogoutOnUserInactivity(): boolean {
    return true;
  }

  public getTokenIfExists(): Observable<string | null> {
    return new Observable<string | null>((observer: Observer<string | null>) => {
      this.auth0Client.getTokenSilently(this.GetTokenOptions()).then((token: string) => {
        observer.next(token);
        observer.complete();
      }).catch(() => {
        observer.next(null);
        observer.complete();
      });
    });
  }
}
